import { IconLeft } from '@assets/icon-left';
import { IconRight } from '@assets/icon-right';
import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { TZDate } from '@date-fns/tz';
import { GetChannelProgramDocument } from '@lib/graphql/generated';
import { useQuery } from '@lib/graphql/urql';
import { composeDateString, formatDate } from '@utils/dateHelpers';
import { add, addDays, isBefore, isSameDay, set, subDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { TV_DATE_RANGES } from 'tvm-config';
import { ChannelShowtime } from './ChannelShowtime';

export interface ChannelDayProps {
  date: Date;
  id: string;
  onAction: (tvChannelShowtime) => void;
  showButton?: string;
  onButtonClick?: () => void;
}

export const ChannelDay = ({ id, date, onAction, showButton, onButtonClick }: ChannelDayProps) => {
  const [now, setNow] = useState<TZDate | null>(null);
  useEffect(() => {
    setNow(new TZDate(new Date(), 'Europe/Vienna'));
  }, []);
  const isToday = now ? isSameDay(date, now) : false;
  const isYesterday = now ? isSameDay(date, subDays(now, 1)) : false;
  const isTomorrow = now ? isSameDay(date, addDays(now, 1)) : false;

  const from = composeDateString(set(date, { hours: 6, minutes: 0, seconds: 0, milliseconds: 0 }));
  const to = composeDateString(set(add(date, { days: 1 }), { hours: 6, minutes: 0, seconds: 0, milliseconds: 0 }));

  const [{ data, fetching: loading }] = useQuery({
    query: GetChannelProgramDocument,
    variables: {
      amount: 100,
      id,
      from,
      to,
      includeImage: true,
    },
  });

  const channelShowtimes = data?.channelShowtimes;

  type rangeTime = { hours: number; minutes: number };

  const entriesInRange = (from: rangeTime, to: rangeTime) => {
    const daysAdded = from.hours > to.hours ? 1 : 0;
    const startTime = set(new Date(date), {
      hours: from.hours,
      minutes: from.minutes || 0,
      seconds: 0,
      milliseconds: 0,
    });
    const endTime = set(add(new Date(date), { days: to.hours - 1 < 0 ? 0 : daysAdded }), {
      hours: (!to.minutes || to.minutes === 0 ? to.hours - 1 : to.hours) % 24,
      minutes: to.minutes && to.minutes > 0 ? to.minutes - 1 : 59,
      seconds: 59,
      milliseconds: 999,
    });

    const entries = channelShowtimes?.data?.filter(
      (e) => !isBefore(new Date(e.start), startTime) && isBefore(new Date(e.start), endTime),
    );
    return entries;
  };

  return (
    <div>
      <div
        className={twMerge(
          'select-none text-center font-bold text-xl uppercase tracking-2px',
          showButton && 'grid grid-cols-[1rem_1fr_1rem] gap-3',
        )}
      >
        {showButton ? (
          showButton === 'prev' ? (
            <div
              className="grid cursor-pointer items-center"
              role="button"
              tabIndex={0}
              onClick={onButtonClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onButtonClick();
              }}
            >
              <IconLeft classProps={{ root: 'hover:opacity-100 opacity-60 border-none w-5 h-5' }} />
            </div>
          ) : (
            <div></div>
          )
        ) : (
          false
        )}
        <h2 className={twMerge('p-3', isToday && 'rounded bg-primary text-white')}>
          {isToday
            ? 'Heute'
            : isYesterday
              ? 'Gestern'
              : isTomorrow
                ? 'Morgen'
                : formatDate(date.toDateString(), { dateStyle: 'medium' })}
        </h2>
        {showButton ? (
          showButton === 'next' ? (
            <div
              className="grid cursor-pointer items-center"
              role="button"
              tabIndex={0}
              onClick={onButtonClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onButtonClick();
              }}
            >
              <IconRight classProps={{ root: 'hover:opacity-100 opacity-60 border-none w-5 h-5' }} />
            </div>
          ) : (
            <div></div>
          )
        ) : (
          false
        )}
      </div>
      {TV_DATE_RANGES.map((range, index) => (
        <div key={index}>
          <div className="select-none pt-3 pb-2 text-center font-bold text-gray-400 text-sm uppercase tracking-2px">
            {range.name}
          </div>
          <LoadingIndicator visible={loading} />
          <div className="mt-2 grid gap-2">
            {channelShowtimes?.data &&
              entriesInRange(
                { hours: range.firstHour, minutes: range.firstMinute || 0 },
                {
                  hours: TV_DATE_RANGES[(index + 1) % TV_DATE_RANGES.length]?.firstHour,
                  minutes: TV_DATE_RANGES[(index + 1) % TV_DATE_RANGES.length]?.firstMinute || 0,
                },
              ).map((showtime) => (
                <ChannelShowtime
                  key={showtime.id}
                  showtime={showtime}
                  onAction={(showtimeEntry) => {
                    onAction(showtimeEntry);
                  }}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};
